import { getFromWindowRef } from '@avantia/client-and-server-utilities';
import { useState } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { mockAnswerSetKeys, MockPolicyDataMode, multiplePoliciesMode, singlePolicyMode } from '../clientMockData';
import { customerDetailsKeyName } from '../handlers/handlerTypes';
import {
  clearMockPolicyDataModeCookie,
  getMockPolicyDataModeCookie,
  setMockPolicyDataModeCookie
} from '../handlers/handlerUtilities';
import {
  allPoliciesLabel,
  MockPolicyKey,
  mockPolicyLabels,
  multiplePoliciesModeLabel,
  singlePolicyModeLabel
} from '../mockPolicies';
import { PerilType } from '../types/policyTypes';
import { questionIds } from '../types/questionConstants';
import './mockDataModeIndicator.scss';

const modes: { value: MockPolicyDataMode; label: string }[] = [
  { value: multiplePoliciesMode, label: multiplePoliciesModeLabel },
  { value: singlePolicyMode, label: singlePolicyModeLabel }
];

const policyKeys = Object.keys(mockPolicyLabels) as MockPolicyKey[];

const noAnswersLabel = 'None';

function reloadPage(): void {
  const { location: windowLocation } = getFromWindowRef<{ location: Location }>(['location']) || {};
  if (windowLocation) {
    windowLocation.reload();
  }
}

// A plain reload would load the existing persisted answers (keyed by the answersId cookie), so the
// mock answers are never injected. Navigating to the journey root with a customerDetailsKey discards
// the current answersId and forces a fresh new-journey request, which is the only path that injects
// the selected peril's mock answers. This mirrors the #mockDataMode redirect in pageComponentUtilities.
function startNewJourney(): void {
  const { location: windowLocation } = getFromWindowRef<{ location: Location }>(['location']) || {};
  if (windowLocation) {
    windowLocation.replace(`${windowLocation.pathname}?${customerDetailsKeyName}=1`);
  }
}

export function MockDataModeIndicator(): JSX.Element {
  const scoreTotal = useSelector((state: any) => {
    const answers = state.questionSet && state.questionSet.answers;
    const scoreTotalAnswer = answers && answers[questionIds.scoreTotal];
    return scoreTotalAnswer ? scoreTotalAnswer.customer : null;
  });

  const current = getMockPolicyDataModeCookie();
  const [selectedMode, setSelectedMode] = useState<MockPolicyDataMode>(current ? current.mode : multiplePoliciesMode);
  const [selectedPolicyKey, setSelectedPolicyKey] = useState<MockPolicyKey | undefined>(
    current ? (current.policyKey as MockPolicyKey | undefined) : undefined
  );
  const [selectedAnswersKey, setSelectedAnswersKey] = useState<keyof PerilType | undefined>(
    current ? current.answersKey : undefined
  );
  const [isCollapsed, setIsCollapsed] = useState(true);

  function applyMode(mode: MockPolicyDataMode, policyKey?: MockPolicyKey, answersKey?: keyof PerilType): void {
    setSelectedMode(mode);
    setSelectedPolicyKey(policyKey);
    setSelectedAnswersKey(answersKey);
    setMockPolicyDataModeCookie({ mode, policyKey, answersKey });
    startNewJourney();
  }

  const selectedModeLabel = selectedMode === multiplePoliciesMode ? multiplePoliciesModeLabel : singlePolicyModeLabel;
  const modeDropdown = (
    <DropdownButton id="mock-data-mode-dropdown" title={selectedModeLabel} variant="primary">
      {modes.map(({ value, label }) => (
        <Dropdown.Item key={value} as="button" onClick={() => applyMode(value, selectedPolicyKey, selectedAnswersKey)}>
          {label}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  );

  const currentPolicyLabel = selectedPolicyKey ? mockPolicyLabels[selectedPolicyKey] : allPoliciesLabel;
  const policyDropdown = (
    <DropdownButton id="mock-policy-dropdown" title={currentPolicyLabel} variant="primary">
      {policyKeys.map((key) => (
        <Dropdown.Item key={key} as="button" onClick={() => applyMode(singlePolicyMode, key, selectedAnswersKey)}>
          {mockPolicyLabels[key]}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  );

  const currentAnswersLabel = selectedAnswersKey ? selectedAnswersKey : noAnswersLabel;
  const answersDropdown = (
    <DropdownButton id="mock-answers-dropdown" title={currentAnswersLabel} variant="primary">
      <Dropdown.Item
        key={noAnswersLabel}
        as="button"
        onClick={() => applyMode(selectedMode, selectedPolicyKey, undefined)}
      >
        {noAnswersLabel}
      </Dropdown.Item>
      {mockAnswerSetKeys.map((key) => (
        <Dropdown.Item key={key} as="button" onClick={() => applyMode(selectedMode, selectedPolicyKey, key)}>
          {key}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  );

  return (
    <div id="hp-mock-data-indicator">
      {!isCollapsed && (
        <button id="hp-show-mock-data-button" onClick={() => setIsCollapsed(true)}>
          &#x25BC;
        </button>
      )}
      {isCollapsed && (
        <>
          <div className="mock-data-header">
            <div>Test Console</div>
            <button id="hp-hide-mock-data-button" onClick={() => setIsCollapsed(false)}>
              &#x25B2;
            </button>
          </div>
          {scoreTotal !== null && <div className="mock-data-score">Score: {scoreTotal}</div>}
          <div>Mode:</div>
          {modeDropdown}
          <div>Policy:</div>
          {policyDropdown}
          <div>Answers:</div>
          {answersDropdown}
          <button
            className="hp-exit-button"
            onClick={() => {
              clearMockPolicyDataModeCookie();
              reloadPage();
            }}
          >
            Exit
          </button>
        </>
      )}
    </div>
  );
}
